import {
  Component,
  h,
  Prop,
  State,
  Event,
  EventEmitter,
  Element,
  getAssetPath
} from "@stencil/core";

import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { AccordionModel } from "@genexus/chameleon-controls-library";

import { Locale } from "../../../common/locale";

const IOS_ID = "ios";
const ANDROID_ID = "android";

const APP_STORE_BADGE = getAssetPath(
  "./gx-ide-assets/launchpad-native-apps/images/app-store-badge.svg"
);
const ANDROID_LOGO = getAssetPath(
  "./gx-ide-assets/launchpad-native-apps/images/android-logo.svg"
);
const MAC_LOGO = getAssetPath(
  "./gx-ide-assets/launchpad-native-apps/images/mac-logo.svg"
);

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/accordion",
  "components/button",
  "components/dialog",
  "components/icon",
  "utils/elevation",
  "utils/layout",
  "utils/spacing",
  "utils/typography"
];

const QR_ICON = getIconPath({
  category: "system",
  name: "qr-code",
  colorType: "interactive"
});

@Component({
  tag: "gx-ide-launchpad-native-apps",
  styleUrl: "launchpad-native-apps.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/launchpad-native-apps"]
})
export class GxIdeLaunchpadNativeApps {
  #componentLocale: any;
  #ACCORDION_MODEL: AccordionModel = [];
  #timeoutIds: Set<number> = new Set();

  @Element() el: HTMLGxIdeLaunchpadNativeAppsElement;

  /**
   * Array of platform sections (ios, android)
   */
  @Prop() readonly objects: PlatformSection[] = [];

  /**
   * Filter value for searching objects
   */
  @State() filterValue: string = "";

  /**
   * Sorting order for recent objects table
   */
  @State() order: { orderCriteria: string | null; ascending: boolean | null } =
    {
      orderCriteria: null,
      ascending: null
    };

  /**
   * Current page for pagination
   */
  @State() page: number = 1;

  /**
   * Items per page for pagination
   */
  @State() perPage: number = 10;

  /**
   * Event emitted when copying to clipboard - like the original version
   */
  @Event() copyToClipboard: EventEmitter<{ url: string }>;

  /**
   * Event emitted when opening app store - like the original version
   */
  @Event() openOnTheAppStore: EventEmitter<{ url: string }>;

  /**
   * Callback function for object selection (passed from parent)
   */
  @Prop() onObjectSelected?: (event: any) => void;

  /**
   * State to track copied messages for each object
   */
  @State() showCopiedMessage: { [key: string]: boolean } = {};

  /**
   * State to control QR dialog visibility
   */
  @State() showQRDialog: boolean = false;

  /**
   * State to store the selected object for QR display
   */
  @State() selectedObjectForQR: NativeAppObject | null = null;

  async componentWillLoad() {
    try {
      this.#componentLocale = await Locale.getComponentStrings(this.el);
    } catch (error) {
      console.error("Error loading locale:", error);
      this.#componentLocale = {};
    }

    this.#ACCORDION_MODEL = [
      {
        id: IOS_ID,
        caption: "iOS",
        expanded: true
      },
      {
        id: ANDROID_ID,
        caption: "Android",
        expanded: true
      }
    ];
  }

  #getQRFillColor(): string {
    const computedStyle = getComputedStyle(document.documentElement);
    return (
      computedStyle.getPropertyValue("--color-text-neutral-default").trim() ||
      computedStyle.getPropertyValue("--color-neutral-400").trim() ||
      "#49566d"
    );
  }

  #getQRBackgroundColor(): string {
    const computedStyle = getComputedStyle(document.documentElement);
    return (
      computedStyle.getPropertyValue("--color-neutral-1600").trim() || "#ffffff"
    );
  }

  #getCopyIcon(): string {
    return getIconPath({
      category: "system",
      name: "copy",
      colorType: "primary"
    });
  }

  /**
   * Copies text to clipboard and emits event - like the original version
   */
  #copyToClipboardFunc(object: NativeAppObject) {
    this.copyToClipboard.emit({
      url: object.url
    });

    if (object.url) {
      navigator.clipboard
        .writeText(object.url)
        .then(() => {
          const objectKey = `${object.name}-${object.url}`;

          this.showCopiedMessage = {
            ...this.showCopiedMessage,
            [objectKey]: true
          };

          const timeoutId = window.setTimeout(() => {
            this.showCopiedMessage = {
              ...this.showCopiedMessage,
              [objectKey]: false
            };
            this.#timeoutIds.delete(timeoutId);
          }, 2000);

          this.#timeoutIds.add(timeoutId);
        })
        .catch(err => {
          console.error("Failed to copy text: ", err);
        });
    }
  }

  /**
   * Opens app store and emits event - like the original version
   */
  #openAppStoreFunc(object: NativeAppObject) {
    this.openOnTheAppStore.emit({
      url: object.url
    });

    if (object.url) {
      window.open(object.url, "_blank");
    }
  }

  /**
   * Handles QR button click
   */
  #handleQRClick(object: NativeAppObject) {
    this.selectedObjectForQR = object;
    this.showQRDialog = true;
  }

  /**
   * Handles closing QR dialog
   */
  #handleCloseQRDialog() {
    this.showQRDialog = false;
    this.selectedObjectForQR = null;
  }

  /**
   * Handles object click and calls parent callback
   */
  #handleObjectClick(object: NativeAppObject) {
    if (object.key && this.onObjectSelected) {
      // Send the full key object as string in format "type|id"
      const keyString = `${object.key.type}|${object.key.id}`;
      this.onObjectSelected({ detail: { key: keyString } });
    }
  }

  /**
   * Cleanup method to prevent memory leaks
   */
  disconnectedCallback() {
    if (this.#timeoutIds) {
      this.#timeoutIds.forEach(timeoutId => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
      this.#timeoutIds.clear();
    }
  }

  /**
   * Renders platform section (iOS or Android) with grid layout like the original version
   */
  #renderPlatformSection(platformType: string) {
    const platformIcon =
      platformType === IOS_ID ? (
        <img src={MAC_LOGO} alt="Apple iOS Logo"></img>
      ) : (
        <img src={ANDROID_LOGO} alt="Android Logo"></img>
      );

    const platformSection = this.objects.find(obj => obj.type === platformType);

    if (!platformSection || platformSection.objects.length === 0) {
      return (
        <div class="no-results">
          <p class="body-regular-s">
            {this.#componentLocale?.noPlatformApps?.replace(
              "{platformType}",
              platformType
            ) || `No ${platformType} applications available`}
          </p>
        </div>
      );
    }

    return (
      <div class="platform-grid">
        {platformSection.objects.map((object, index) => (
          <div
            key={index}
            class="object-card elevation-1"
            tabindex="0"
            onClick={() => this.#handleObjectClick(object)}
          >
            <div class="card-content">
              <div class="info-section">
                <div class="app-info">
                  <h1 class="name body-semi-bold-s">{object.name}</h1>
                  {object.description && (
                    <p class="description body-regular-xs">
                      {object.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div class="card-actions">
              {object.url && (
                <div class="platform-section">
                  {object["app-store-badge"] ? (
                    <img
                      src={APP_STORE_BADGE}
                      alt="Download on the App Store"
                      class="app__apple-store"
                      onClick={() => this.#openAppStoreFunc(object)}
                    />
                  ) : (
                    platformIcon
                  )}
                  <button
                    class="button-secondary button-icon-and-text"
                    onClick={() => this.#handleQRClick(object)}
                    aria-label="Show QR Code"
                  >
                    <ch-image class="icon-m" src={QR_ICON} type="mask" />
                    {this.#componentLocale?.showQR || "Show QR"}
                  </button>
                </div>
              )}
              <button
                class="button-tertiary button-icon-and-text copy-button"
                onClick={() => this.#copyToClipboardFunc(object)}
                aria-label="Copy to clipboard"
              >
                <ch-image class="icon-m" src={this.#getCopyIcon()} />
                {this.showCopiedMessage[`${object.name}-${object.url}`]
                  ? this.#componentLocale?.copied || "Copied!"
                  : this.#componentLocale?.copy || "Copy"}
              </button>
            </div>
          </div>
        ))}
        {
          // WA: Prevent card take full width if there is only one card
          platformSection.objects.length === 1 && <div></div>
        }
      </div>
    );
  }

  render() {
    if (!this.objects || this.objects.length === 0) {
      <div class="no-data">
        <p class="body-regular-s">
          {this.#componentLocale?.noData ||
            "No native applications data available"}
        </p>
      </div>;
    }

    return [
      <ch-theme model={CSS_BUNDLES}></ch-theme>,
      <div class="main">
        <ch-accordion-render
          class="accordion-filled-header elevation-1 native-apps-accordion"
          model={this.#ACCORDION_MODEL}
        >
          <div slot={IOS_ID} class="accordion-content spacing-body">
            {this.#renderPlatformSection(IOS_ID)}
          </div>
          <div slot={ANDROID_ID} class="accordion-content spacing-body">
            {this.#renderPlatformSection(ANDROID_ID)}
          </div>
        </ch-accordion-render>
      </div>,
      <ch-dialog
        class="dialog"
        show={this.showQRDialog}
        show-header={true}
        closable={true}
        close-button-accessible-name="Close QR code"
        onDialogClosed={() => this.#handleCloseQRDialog()}
      >
        {this.selectedObjectForQR && (
          <div class="qr-container">
            <ch-qr
              value={this.selectedObjectForQR.url || ""}
              size={200}
              error-correction-level="Low"
              class="qr-code"
              fill={this.#getQRFillColor()}
              background={this.#getQRBackgroundColor()}
            ></ch-qr>
          </div>
        )}
      </ch-dialog>
    ];
  }
}

interface NativeAppObject {
  "name": string;
  "module"?: string;
  "type"?: string;
  "description"?: string;
  "platform"?: string;
  "url"?: string;
  "generator"?: string;
  "app-store-badge"?: boolean;
  "key"?: {
    type: string;
    id: string;
  };
}

interface PlatformSection {
  type: string;
  objects: NativeAppObject[];
}
